import * as React from "react"
import { Box } from "@chakra-ui/react"
import {
  BIKE_SERVICE_KEY,
  BIKE_MAINTENANCE_KEY,
  BIKE_DROPZONE_KEY,
  TOPBAR_HEIGHT,
  NAV_WIDTH,
} from "../lib/config"
import { BikeState } from "../lib/types/bike"
import { ZoneEntity } from "../lib/types/zone"
import { Sidebar } from "./Sidebar"
import { createBbox, Coord } from "../lib/helpers/map"
import { useStoredDisclosure } from "../lib/hooks/useStoredDisclosure"
import { useGetInitialBikesMapBbox } from "../lib/hooks/queries/useGetInitialBikesMapBbox"
import { MapBikeEntity } from "../lib/hooks/queries/useGetMapBikesQuery"
import {
  useGetMaintenanceZonesQuery,
  useGetServiceZonesQuery,
  useGetDropzonesQuery,
} from "../lib/hooks/queries/useGetMapZones"
import BikesMapGoogleMap from "./BikesMapGoogleMap"
import { BikesMapSidebar } from "./BikesMapSidebar"
import { MapZoneControl } from "./MapZoneControl"
import { ErrorBoundary } from "./ErrorBoundary"
import { useMapBBox } from "./providers/MapBBoxProvider"
import { BikesMapMapbox } from "./BikesMapMapbox"
import { useMapSettings } from "./providers/MapSettingsProvider"
import { Loading } from "./Loading"

interface BikesMapContainerProps {
  search: string
  bikeStates: BikeState[]
  bikeModels: string[]
  bikeTags: string[]
}

export type HandleSearchUpdateArgs = {
  bikes: MapBikeEntity[]
  bikesLoading: boolean
}

const _BikesMapContainer: React.FC<BikesMapContainerProps> = ({
  search,
  bikeStates,
  bikeModels,
  bikeTags,
}) => {
  const { mapProvider } = useMapSettings()
  const [activeBike, setActiveBike] = React.useState<MapBikeEntity | null>(null)
  const [selectedZone, setSelectedZone] = React.useState<ZoneEntity | undefined>()

  // initial BBox
  const { currentBBox, setCurrentBBox } = useMapBBox()
  const initialBBox = useGetInitialBikesMapBbox()

  React.useEffect(() => setCurrentBBox(initialBBox), [initialBBox, setCurrentBBox])

  const { isOpen: showServiceZones, onToggle: onServiceZoneToggle } = useStoredDisclosure({
    key: BIKE_SERVICE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showDropzones, onToggle: onDropzoneToggle } = useStoredDisclosure({
    key: BIKE_DROPZONE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showMaintenanceZones, onToggle: onMaintenanceToggle } = useStoredDisclosure({
    key: BIKE_MAINTENANCE_KEY,
    defaultIsOpen: true,
  })

  const { serviceZones } = useGetServiceZonesQuery({ enabled: showServiceZones })
  const { dropzones } = useGetDropzonesQuery({ enabled: showDropzones })
  const { maintenanceZones } = useGetMaintenanceZonesQuery({ enabled: showMaintenanceZones })

  const handleSearchUpdate = React.useCallback(
    ({ bikes, bikesLoading }: HandleSearchUpdateArgs) => {
      // When searching, change bbox based off search results
      if (!search) return setActiveBike(null)
      if (bikes && search && !bikesLoading) {
        if (bikes.length === 0) return
        const filteredBikes = bikes.filter((b) => !!b.longitude && !!b.latitude)
        if (filteredBikes.length === 0) return
        setCurrentBBox(createBbox(filteredBikes.map((b) => [b.longitude, b.latitude] as Coord)))

        // If one bike, open marker
        if (filteredBikes.length === 1) {
          setActiveBike(filteredBikes[0])
        } else {
          setActiveBike(null)
        }
      }
    },
    [search, setActiveBike, setCurrentBBox],
  )

  return (
    <Box
      position="relative"
      style={{
        height: `calc(100vh - ${TOPBAR_HEIGHT})`,
        width: `calc(100vw - ${NAV_WIDTH})`,
      }}
    >
      <Sidebar sidebarKey="noa:bikes:map" styleControl>
        <BikesMapSidebar setSelectedZone={setSelectedZone} bikeStates={bikeStates} bikeModels={bikeModels} />
      </Sidebar>
      {!currentBBox ? (
        <Loading w="100%" h="100%" />
      ) : (
        <ErrorBoundary>
          <>
            {mapProvider === "google_maps" ? (
              <BikesMapGoogleMap
                selectedZone={selectedZone}
                showServiceZones={showServiceZones}
                serviceZones={serviceZones}
                showDropzones={showDropzones}
                dropzones={dropzones}
                showMaintenanceZones={showMaintenanceZones}
                maintenanceZones={maintenanceZones}
                search={search}
                bikeStates={bikeStates}
                bikeModels={bikeModels}
                bikeTags={bikeTags}
                handleSearchUpdate={handleSearchUpdate}
                activeBike={activeBike}
              />
            ) : (
              <BikesMapMapbox
                selectedZone={selectedZone}
                showServiceZones={showServiceZones}
                serviceZones={serviceZones}
                showDropzones={showDropzones}
                dropzones={dropzones}
                showMaintenanceZones={showMaintenanceZones}
                maintenanceZones={maintenanceZones}
                search={search}
                bikeStates={bikeStates}
                bikeModels={bikeModels}
                bikeTags={bikeTags}
                handleSearchUpdate={handleSearchUpdate}
                activeBike={activeBike}
              />
            )}
            <MapZoneControl
              showDropzones={showDropzones}
              showServiceZones={showServiceZones}
              showMaintenanceZones={showMaintenanceZones}
              onDropzoneToggle={onDropzoneToggle}
              onServiceZoneToggle={onServiceZoneToggle}
              onMaintenanceToggle={onMaintenanceToggle}
            />
          </>
        </ErrorBoundary>
      )}
    </Box>
  )
}

const BikesMapContainer = React.memo(_BikesMapContainer)

export default React.memo(BikesMapContainer)
